import React from 'react';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './Proposals.css';

// Components
import ProposalsHeader from '../components/ProposalsHeader';
import StatsGrid from '../components/StatsGrid';
import ProposalsFilterBar from '../components/ProposalsFilterBar';
import ProposalsGrid from '../components/ProposalsGrid';
import DeleteRFPModal from '../components/Modals/DeleteRFPModal';
import SuccessToast from '../../../shared/components/ui/SuccessToast';
import ExportModal from '../../rfp/shared/components/ExportModal';
import PPTGenerationModal from '../../ppt/components/PPTGenerationModal';
import CustomLoader from '../../../shared/components/ui/CustomLoader';
import DashboardSkeleton from '../components/Skeletons/DashboardSkeleton';

// Hooks & Utils
import { useProposals } from '../hooks/useProposals';
import { ITEMS_PER_PAGE } from '../constants/proposalUtils';
import { useScrollToTop } from '../../../utils/scrollUtils';

const renderPaginationNumbers = (currentPage, totalPages) => {
    if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 3) {
        return [1, 2, 3, 4, '...', totalPages];
    }
    if (currentPage >= totalPages - 2) {
        return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
};

const Proposals = ({ user, globalSearch }) => {
    const {
        rfpStats, metrics, loading, error,
        searchQuery, setSearchQuery, statusFilter, setStatusFilter,
        currentPage, selectedIds,
        showExport, setShowExport, selectedRfpId,
        itemToDelete, setItemToDelete,
        isDeleting, successToast, isFetchingFullData, fetchingRfpTitle,
        isBulkDownloading, showMoreFilters, setShowMoreFilters,
        moreFiltersRef, handleToggleSelect, handleActionClick,
        confirmDelete, handleBulkDownload,
        filteredResults, totalPages, paginatedData, handlePageChange,
        isNextDisabled, metadata, isFilterChanging, isManager
    } = useProposals(globalSearch, user);
    const navigate = useNavigate();
    const [showPPTModal, setShowPPTModal] = React.useState(false);

    useScrollToTop([statusFilter, currentPage]);

    // Prevent background scrolling when overlay or modal is active
    React.useEffect(() => {
        const isAnyModalOpen = isFetchingFullData || isBulkDownloading || Boolean(itemToDelete) || showExport || showPPTModal;
        if (isAnyModalOpen) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isFetchingFullData, isBulkDownloading, itemToDelete, showExport, showPPTModal]);

    if (loading) return <DashboardSkeleton isManager={isManager} />;

    const totalCount = isManager ? (metadata?.total_count || 0) : filteredResults.length;
    const startRange = totalCount === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const endRange = Math.min(totalCount, currentPage * ITEMS_PER_PAGE);

    return (
        <>
            <div className="proposals-page">
                <ProposalsHeader
                    selectedCount={selectedIds.length}
                    onDeleteBulk={() => setItemToDelete({
                        proposal_title: `${selectedIds.length} selected proposals`,
                        filename: 'selected items',
                        multiple: true
                    })}
                    onDownloadBulk={handleBulkDownload}
                    isBulkDownloading={isBulkDownloading}
                />

                {error && <div className="login-error-message" style={{ margin: '0 0 2rem 0' }}>{error}</div>}

                <StatsGrid metrics={metrics} rfpData={paginatedData} />

                <div className="table-container-main">
                    <ProposalsFilterBar
                        searchQuery={searchQuery}
                        onSearchChange={(val) => {
                            setSearchQuery(val);
                            handlePageChange(1);
                        }}
                        statusFilter={statusFilter}
                        onStatusChange={(key) => {
                            setStatusFilter(key);
                            handlePageChange(1);
                        }}
                        rfpStats={rfpStats}
                        showMoreFilters={showMoreFilters}
                        setShowMoreFilters={setShowMoreFilters}
                        moreFiltersRef={moreFiltersRef}
                        selectedCount={selectedIds.length}
                        onDownloadBulk={handleBulkDownload}
                        onDeleteBulk={() => setItemToDelete({
                            proposal_title: `${selectedIds.length} selected proposals`,
                            filename: 'selected items',
                            multiple: true
                        })}
                        isBulkDownloading={isBulkDownloading}
                    />

                    {isFilterChanging ? (
                        <div className="filter-loader-container">
                            <CustomLoader className="loader-spin" size={40} />
                            <p>Updating proposals...</p>
                        </div>
                    ) : (
                        <ProposalsGrid
                            data={paginatedData}
                            selectedIds={selectedIds}
                            onToggleSelect={handleToggleSelect}
                            onActionClick={handleActionClick}
                        />
                    )}

                    <div className="table-footer" style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>
                        <span className="footer-info" style={{ justifySelf: 'start' }}>
                            Showing {startRange} to {endRange} out of {totalCount} proposals
                        </span>
                        {totalPages > 1 ? (
                            <div className="pagination" style={{ justifySelf: 'center', margin: 0 }}>
                                <button
                                    className="page-nav"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                {renderPaginationNumbers(currentPage, totalPages).map((item, i) => {
                                    if (item === '...') {
                                        return (
                                            <span key={`ellipsis-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', color: '#64748B' }}>
                                                ...
                                            </span>
                                        );
                                    }
                                    return (
                                        <button
                                            key={item}
                                            className={`page-num ${currentPage === item ? 'active' : ''}`}
                                            onClick={() => handlePageChange(item)}
                                        >
                                            {item}
                                        </button>
                                    );
                                })}
                                <button
                                    className="page-nav"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={isNextDisabled}
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        ) : <div></div>}
                        <div></div>
                    </div>
                </div>
            </div>

            {/* Overlays & Modals */}
            {isFetchingFullData && (
                <div
                    className="fetching-overlay"
                    onWheel={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                >
                    <div className="fetching-content">
                        <CustomLoader className="loader-spin" size={48} />
                        <div className="fetching-text">
                            <h3>Opening Proposal</h3>
                            <p>Fetching full details for <strong>{fetchingRfpTitle}</strong>...</p>
                        </div>
                    </div>
                </div>
            )}

            <DeleteRFPModal
                item={itemToDelete}
                isDeleting={isDeleting}
                onConfirm={confirmDelete}
                onCancel={() => setItemToDelete(null)}
            />

            <SuccessToast toast={successToast} />

            {(() => {
                const selectedRfpData = paginatedData.find(item => String(item._id || item.rfp_id || item.id) === String(selectedRfpId));
                return (
                    <>
                        {showExport && (
                            <ExportModal
                                isOpen={showExport}
                                onClose={() => setShowExport(false)}
                                rfpId={selectedRfpId}
                                analysisData={selectedRfpData}
                                onPPTClick={() => {
                                    setShowExport(false);
                                    setShowPPTModal(true);
                                }}
                            />
                        )}
                        <PPTGenerationModal
                            isOpen={showPPTModal}
                            onClose={() => setShowPPTModal(false)}
                            rfpId={selectedRfpId}
                            navigate={navigate}
                            editableData={selectedRfpData}
                        />
                    </>
                );
            })()}

            {isBulkDownloading && (
                <div
                    className="fetching-overlay"
                    style={{ zIndex: 10000 }}
                    onWheel={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                >
                    <div className="fetching-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '2.5rem', borderRadius: '12px', minWidth: '320px', background: 'white', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
                        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CustomLoader className="loader-spin" size={60} />
                            <div style={{ position: 'absolute', color: '#FF5526', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Download size={22} />
                            </div>
                        </div>
                        <div className="fetching-text" style={{ textAlign: 'center', margin: 0 }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1E293B', marginBottom: '4px' }}>Downloading PDF</h3>
                            <p style={{ fontSize: '13px', color: '#64748B', margin: 0 }}>Please wait while we bundle your proposals...</p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Proposals;
